/**
 * VIP Room Model
 * Represents a VIP Room entity with validation and helper methods
 */
export class VIPRoom {
  constructor(data = {}) {
    this._id = data._id || null
    this.name = data.name || ''
    this.pricePerHour = data.pricePerHour || 0
    this.branch = data.branch || null
    this.category = data.category || 'small'
    this.discount = data.discount || 0
    this.isActive = data.isActive !== undefined ? data.isActive : true
    this.createdBy = data.createdBy || null
    this.createdAt = data.createdAt || null
    this.updatedAt = data.updatedAt || null
  }

  /**
   * Validate VIP Room data
   * @returns {Object} - { isValid: boolean, errors: object }
   */
  validate() {
    const errors = {}

    if (!this.name || !this.name.trim()) {
      errors.name = 'Room name is required'
    }

    if (!this.pricePerHour || this.pricePerHour <= 0) {
      errors.pricePerHour = 'Valid price is required'
    }

    if (!this.branch) {
      errors.branch = 'Branch is required'
    }

    if (this.discount < 0 || this.discount > 100) {
      errors.discount = 'Discount must be between 0-100'
    }

    const validCategories = ['small', 'medium', 'large', 'premium']
    if (!validCategories.includes(this.category)) {
      errors.category = 'Invalid category'
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  /**
   * Convert to API payload format
   * @returns {Object} - Payload ready for API POST/PATCH
   */
  toPayload() {
    return {
      name: this.name,
      pricePerHour: parseFloat(this.pricePerHour),
      branch: typeof this.branch === 'object' ? this.branch._id : this.branch,
      category: this.category,
      discount: parseInt(this.discount),
      isActive: this.isActive
    }
  }

  /**
   * Create VIPRoom instance from API response
   * @param {Object} data - API response data
   * @returns {VIPRoom}
   */
  static fromApiResponse(data) {
    return new VIPRoom(data)
  }

  /**
   * Get category badge color class
   * @returns {string}
   */
  getCategoryBadgeColor() {
    const colors = {
      small: 'bg-blue-100 text-blue-800',
      medium: 'bg-purple-100 text-purple-800',
      large: 'bg-green-100 text-green-800',
      premium: 'bg-yellow-100 text-yellow-800'
    }
    return colors[this.category] || 'bg-gray-100 text-gray-800'
  }

  /**
   * Get formatted category name
   * @returns {string}
   */
  getFormattedCategory() {
    return this.category.charAt(0).toUpperCase() + this.category.slice(1)
  }

  /**
   * Get formatted price
   * @returns {string}
   */
  getFormattedPrice() {
    return `Rs. ${this.pricePerHour.toLocaleString()}`
  }

  /**
   * Get status badge color class
   * @returns {string}
   */
  getStatusBadgeColor() {
    return this.isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800'
  }

  /**
   * Get status text
   * @returns {string}
   */
  getStatusText() {
    return this.isActive ? 'Active' : 'Inactive'
  }

  /**
   * Get branch name
   * @returns {string}
   */
  getBranchName() {
    if (typeof this.branch === 'object' && this.branch !== null) {
      return this.branch.name || 'N/A'
    }
    return 'N/A'
  }
}

/**
 * Initial form data for creating a new VIP Room
 */
export const initialVIPRoomFormData = {
  name: '',
  pricePerHour: '',
  branch: '',
  category: 'small',
  discount: '',
  isActive: true
}

/**
 * VIP Room categories
 */
export const VIP_ROOM_CATEGORIES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'premium', label: 'Premium' }
]
